import React, {useState, useContext} from "react";
import {
    Box, Stack, Typography, Button, Collapse,
    TextField, CircularProgress, Alert, Tooltip
} from "@mui/material";
import {IconChevronRight, IconPlayerPlay, IconTrash, IconAlertTriangle} from "@tabler/icons-react";
import {Context} from "../../../../context/AppContext";
import {DescriptionPanel, ResultPanel, SectionHeader} from "./mcp-detail-shared";
import useConfirm from "../../../../hooks/useConfirm";

const ToolCard = ({tool, server, onDelete}) => {
    const {callMCPTool} = useContext(Context);

    const [schemaOpen, setSchemaOpen] = useState(false);
    const [executeOpen, setExecuteOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);


    const schema = tool.input_schema || tool.inputSchema || {};
    const properties = schema.properties || {};
    const required = schema.required || [];

    const isActive = tool.is_active ?? true;


    const buildDefaultArgs = () =>
        Object.fromEntries(
            Object.entries(properties).map(([key, val]) => {
                if ("default" in val) return [key, val.default ?? ""];
                const type = val.type || val.anyOf?.[0]?.type;
                if (type === "integer" || type === "number") return [key, ""];
                if (type === "boolean") return [key, false];
                return [key, ""];
            })
        );

    const [fieldValues, setFieldValues] = useState(buildDefaultArgs);
    const [fieldErrors, setFieldErrors] = useState({});

    const handleFieldChange = (key, value) => {
        setFieldValues(prev => ({...prev, [key]: value}));
        if (required.includes(key) && !value && value !== 0) {
            setFieldErrors(prev => ({...prev, [key]: "Required"}));
        } else {
            setFieldErrors(prev => ({...prev, [key]: null}));
        }
    };

    const validate = () => {
        const errors = {};
        required.forEach(key => {
            const val = fieldValues[key];
            if (val === "" || val === null || val === undefined) errors[key] = "Required";
        });
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const buildArgs = () =>
        Object.fromEntries(
            Object.entries(fieldValues).map(([key, val]) => {
                const prop = properties[key];
                const type = prop?.type || prop?.anyOf?.[0]?.type;
                if ((type === "integer" || type === "number") && val !== "" && val !== null)
                    return [key, Number(val)];
                if (val === "") return [key, null];
                return [key, val];
            })
        );

    const handleExecute = async () => {
        if (!validate()) return;
        setLoading(true);
        setResult(null);
        setError(null);
        console.log("tool:", tool)
        try {
            const data = await callMCPTool({server_id: server.id, tool_name: tool.name, arguments: buildArgs()});
            setResult(data);
        } catch (e) {
            setError(e.response?.data.detail || e.message || "Tool call failed");
        } finally {
            setLoading(false);
        }
    };

    const getFieldType = (prop) => {
        const type = prop?.type || prop?.anyOf?.find(t => t.type !== "null")?.type;
        if (type === "integer" || type === "number") return "number";
        return "text";
    };

    return (
        <Box className={"border-shadow"} sx={{borderRadius: 2, p: 2}}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                <Box sx={{flex: 1, display: "flex", alignItems: "center", gap: 0.5}}>
                    {!isActive && (
                        <Tooltip title={"This tool may not be active"}>
                            <IconAlertTriangle size={15} color="#d97706"/>
                        </Tooltip>

                    )}
                    <Typography fontSize={13} fontWeight={600}>{tool.name}</Typography>
                </Box>
                <Stack direction="row" gap={0.5} flexShrink={0} alignItems="center">
                    {/*{tool.inputSchema && (*/}
                    {/*    <Button*/}
                    {/*        size="small"*/}
                    {/*        startIcon={<IconCode size={13}/>}*/}

                    {/*        onClick={() => setSchemaOpen(v => !v)}*/}
                    {/*        sx={{fontSize: 11, textTransform: "none", color: "text.secondary"}}*/}
                    {/*    >*/}
                    {/*        Schema*/}
                    {/*    </Button>*/}
                    {/*)}*/}
                    <Button
                        size="small"
                        // variant="contained" color="primary"
                        onClick={() => {
                            setExecuteOpen(v => !v);
                            setResult(null);
                            setError(null);
                            setSchemaOpen(v => !v)
                        }}
                        endIcon={<IconChevronRight size={13} style={{
                            transition: "transform 0.15s",
                            transform: schemaOpen ? "rotate(90deg)" : "rotate(0deg)"
                        }}/>}
                        sx={{fontSize: 11, textTransform: "none", borderRadius: 0, color: "grey.600"}}
                    >
                        {executeOpen ? "Show less" : "Show more"}
                    </Button>
                </Stack>
            </Stack>

            {tool.description && <DescriptionPanel open={executeOpen} description={tool.description}/>}

            <Collapse in={schemaOpen}>

                <Box component="pre" sx={{
                    mt: 1.5, p: 1.5, bgcolor: "grey.100", borderRadius: 1,
                    fontSize: 12, fontFamily: "monospace", overflowX: "auto",
                    color: "text.secondary", whiteSpace: "pre-wrap", wordBreak: "break-all"
                }}>
                    {JSON.stringify(tool.input_schema || tool.inputSchema, null, 2)}
                </Box>
            </Collapse>

            <Collapse in={executeOpen}>
                <Box sx={{mt: 1.5, display: "flex", flexDirection: "column", gap: 1}}>
                    {!isActive ? (
                        <>
                            <Alert severity="warning" sx={{fontSize: 12}}>
                                This tool is currently inactive and cannot be executed.
                            </Alert>
                            <Box mt={0.5} sx={{ml: "auto"}}>
                                <Button
                                    variant="contained" color="secondary" size="small"
                                    startIcon={<IconTrash size={13}/>}
                                    onClick={() => onDelete(tool.id)}
                                    sx={{textTransform: "none", borderRadius: 2, fontSize: 12}}
                                >
                                    Delete Tool
                                </Button>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Typography fontSize={11} fontWeight={700} textTransform="uppercase" letterSpacing={0.5}
                                        color="text.secondary">
                                Arguments
                            </Typography>
                            {Object.keys(properties).length === 0 ? (
                                <Typography fontSize={12} color="text.secondary" fontStyle="italic">
                                    This tool requires no arguments.
                                </Typography>
                            ) : Object.entries(properties).map(([key, prop]) => {
                                const isRequired = required.includes(key);
                                return (
                                    <Box key={key}>
                                        <Stack direction="row" alignItems="center" gap={0.5} mb={0.5}>
                                            <Typography fontSize={12} fontFamily="monospace"
                                                        fontWeight={500}>{key}</Typography>
                                            {isRequired && (
                                                <Typography component="span" sx={{
                                                    color: "error.main",
                                                    fontSize: 12,
                                                    fontWeight: 700
                                                }}>*</Typography>
                                            )}
                                            <Typography fontSize={11} color="text.secondary">
                                                {prop.title && prop.title !== key ? `— ${prop.title}` : ""}
                                            </Typography>
                                        </Stack>
                                        <TextField
                                            size="small"
                                            type={getFieldType(prop) === "number" ? "number" : "text"}
                                            value={fieldValues[key] ?? ""}
                                            onChange={e => handleFieldChange(key, e.target.value)}
                                            error={!!fieldErrors[key]}
                                            helperText={fieldErrors[key]}
                                            placeholder={isRequired ? "Required" : "Optional"}
                                            InputProps={{sx: {fontFamily: "monospace", fontSize: 12, borderRadius: 1}}}
                                            sx={{
                                                "& .MuiOutlinedInput-root .fieldset": {
                                                    borderColor: isRequired && !fieldValues[key] ? "error.light" : undefined,
                                                    borderWidth: isRequired && !fieldValues[key] ? 1.5 : 1,
                                                },
                                            }}
                                        />
                                    </Box>
                                );
                            })}
                            {error && <Alert severity="error" sx={{fontSize: 12}}>{error}</Alert>}
                            {result !== null && <ResultPanel result={result}/>}
                            <Box mt={0.5} sx={{ml: "auto"}}>
                                <Button
                                    variant="contained" color="secondary" size="small"
                                    startIcon={loading ? <CircularProgress size={12} color="inherit"/> :
                                        <IconPlayerPlay size={13}/>}
                                    onClick={handleExecute}
                                    disabled={loading}
                                    sx={{textTransform: "none", borderRadius: 2, fontSize: 12}}
                                >
                                    {loading ? "Running..." : "Run Tool"}
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
};

export const MCPToolsSection = ({tools, server, onToolDeleted}) => {
    const [open, setOpen] = useState(true);
    const {deleteInactiveTool} = useContext(Context);
    const confirm = useConfirm();

    const handleDelete = async (toolId) => {
        const confirmed = await confirm({
            title: "Delete Tool",
            message: "Are you sure you want to delete this inactive tool? This action cannot be undone.",
        });
        if (!confirmed) return;
        try {
            await deleteInactiveTool(toolId);
            onToolDeleted?.();
        } catch (e) {
            console.error("Failed to delete tool:", e);
        }
    };
    // const fakeTool = {
    //     id: 999,
    //     name: "booking_get_doctor_by_id_2",
    //     description: "Get full details for a specific doctor by their numeric ID.",
    //     is_active: false,
    //     input_schema: {
    //         properties: {doctor_id: {type: "integer", title: "Doctor Id"}},
    //         required: ["doctor_id"],
    //         title: "booking_get_doctor_by_idArguments",
    //         type: "object"
    //     }
    // };
    return (
        <Box sx={{py: 2}}>
            <SectionHeader label="Tools" count={tools.length} open={open} onToggle={() => setOpen(v => !v)}/>
            <Collapse in={open}>
                <Stack gap={1.5}>
                    {/*<ToolCard tool={fakeTool} server={server} onDelete={handleDelete}/>*/}
                    {tools.map(tool => (
                        <ToolCard key={tool.name} tool={tool} server={server} onDelete={handleDelete}/>
                    ))}
                </Stack>
            </Collapse>
        </Box>
    );
};

export default MCPToolsSection;